import { Card, Box } from "@mui/material";
import classNames from "classnames";
import React from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { useSnapshot } from "valtio";

import { designStore, generalConfigStore, setSelectedCategory } from "@store";

import { data } from "@pages/GeneralConfig/Design/Content/OrderTaking/fastFoodCard";

import {
    CATEGORY_BORDER_STYLE_SVG,
    CATEGORY_BORDER_STYLE_UNI,
    CATEGORY_DISPLAY_MODE_2,
    CATEGORY_SVG_BORDER_WIDTH,
    CATEGORY_SVG_BORDER_SLICE,
} from "@constants/index";

import {
    hexToRgba,
    makeResolveColor,
    safeGetSvgBorderCssUrl,
    DEFAULT_DISPLAY_TEXT_COLOR,
} from "./displayStyleHelpers";

import "./category.css";
import "swiper/swiper.min.css";

// Référence stable au niveau module (data est un import figé) : évite de
// recréer le tableau à chaque render et de faire re-tourner l'effet d'init.
const GLOBAL_CATEGORIES = data.categories;

export function CategoriesSwiperWrapper(): JSX.Element {
    const globalCategories = GLOBAL_CATEGORIES;

    const {
        isOrderTakingCategoryTitleActive,
        isOrderTakingTopBannerActive,
        project,
    } = useSnapshot(generalConfigStore);

    const {
        selectedCategory,
        categoryDisplayMode,
        categoryDisplayBackgroundColor,
        categoryDisplayBackgroundOpacity,
        categoryDisplaySelectedTextColor,
        categoryDisplayBorderStyle,
        categoryDisplayBorderColor,
        categoryDisplaySvgBorder,
    } = useSnapshot(designStore);

    const generalDesign = project.template.content.generalDesign;
    const resolvePrimary = makeResolveColor(generalDesign);

    const isMode2 = categoryDisplayMode === CATEGORY_DISPLAY_MODE_2;
    // Garde : on n'utilise la valeur stockée que si c'est bien une data URL SVG
    // base64 (et seulement en `url()` CSS), comme dans CardWrapper. Empêche
    // l'injection d'une valeur svgBorder corrompue (template compromis).
    const safeCategorySvgBorderUrl = safeGetSvgBorderCssUrl(
        categoryDisplaySvgBorder
    );

    React.useEffect(() => {
        if (globalCategories.length) {
            setSelectedCategory(globalCategories[0]);
        }
    }, [globalCategories]);

    return (
        <Swiper
            style={{
                height: [isOrderTakingTopBannerActive].some((v) => v === false)
                    ? "454px"
                    : "400px",

                marginTop: [isOrderTakingTopBannerActive].some(
                    (v) => v === false
                )
                    ? "120px"
                    : "50px",
            }}
            className="category-swiper__clz"
            role="navigation"
            aria-label="Categories"
            spaceBetween={1}
            speed={500}
            grabCursor
            centeredSlides
            slidesPerView={5}
            loop
            direction="vertical"
            centeredSlidesBounds
            autoplay={{
                delay: 300,
                disableOnInteraction: false,
                reverseDirection: false,
            }}
            slideToClickedSlide
            onSlideChangeTransitionStart={(swiper) => swiper.autoplay.stop()}
        >
            {globalCategories.map((category, index) => {
                const isActive = category.name === selectedCategory?.name;
                return (
                    // category.name est l'identité stable (unique dans
                    // data.categories) déjà utilisée pour l'état actif ; évite
                    // que React/Swiper (loop=true clone les slides) réconcilie
                    // sur la position et colle l'état au mauvais slide.
                    <SwiperSlide key={category.name}>
                        <Box
                            className="d-flex flex-column justify-content-center align-items-center"
                            style={{
                                gap: isOrderTakingCategoryTitleActive
                                    ? "5px"
                                    : undefined,
                                cursor: "pointer",
                            }}
                        >
                            {/* ================= WRAPPER ================= */}
                            <Box
                                role="button"
                                tabIndex={0}
                                aria-label={category.name}
                                aria-pressed={isActive}
                                onClick={() => setSelectedCategory(category)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        setSelectedCategory(category);
                                    }
                                }}
                                sx={{
                                    position: "relative",
                                    width: "57px",
                                    height: "56px",
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                }}
                            >
                                {/* ================= SVG BORDER ================= */}
                                {/* Le SVG est dessiné uniquement sur le contour
                                    de la carte via border-image (pas en fond). */}
                                {isActive &&
                                    categoryDisplayBorderStyle ===
                                        CATEGORY_BORDER_STYLE_SVG &&
                                    safeCategorySvgBorderUrl.length > 0 && (
                                        <Box
                                            sx={{
                                                position: "absolute",
                                                inset: 0,
                                                borderStyle: "solid",
                                                borderWidth: `${CATEGORY_SVG_BORDER_WIDTH}px`,
                                                borderImage: `url(${safeCategorySvgBorderUrl}) ${CATEGORY_SVG_BORDER_SLICE} stretch`,
                                                pointerEvents: "none",
                                                zIndex: 3,
                                            }}
                                        />
                                    )}

                                {/* ================= UNI BORDER ================= */}
                                {/* Au-dessus de la carte (zIndex 2) pour que la
                                    bordure reste visible par-dessus le fond. */}
                                {isActive &&
                                    categoryDisplayBorderStyle ===
                                        CATEGORY_BORDER_STYLE_UNI && (
                                        <Box
                                            sx={{
                                                position: "absolute",
                                                inset: 0,
                                                border: `2px solid ${resolvePrimary(
                                                    categoryDisplayBorderColor
                                                )}`,
                                                borderRadius: "9px",
                                                pointerEvents: "none",
                                                zIndex: 3,
                                            }}
                                        />
                                    )}

                                {/* ================= CARD ================= */}
                                <Card
                                    className={classNames({
                                        "category-card__clz":
                                            !isActive || isMode2,
                                        "active-category-card__clz":
                                            isActive && !isMode2,
                                    })}
                                    sx={{
                                        width: "57px",
                                        height: "56px",
                                        borderRadius: "9px",
                                        boxShadow: "0px 3px 6px #00000029",
                                        background:
                                            isMode2 && isActive
                                                ? hexToRgba(
                                                      resolvePrimary(
                                                          categoryDisplayBackgroundColor
                                                      ),
                                                      categoryDisplayBackgroundOpacity
                                                  )
                                                : "transparent",
                                        zIndex: 2,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: "100%",
                                            height: "100%",
                                            display: "flex",
                                            justifyContent: "center",
                                            alignItems: "center",
                                        }}
                                    >
                                        <img
                                            src={category.image}
                                            alt={category.name}
                                            style={{
                                                height: "43px",
                                            }}
                                        />
                                    </Box>
                                </Card>
                            </Box>

                            {/* ================= TITLE ================= */}
                            {isOrderTakingCategoryTitleActive && (
                                <Box
                                    sx={{
                                        font:
                                            "normal normal bold 10px/15px Segoe UI",
                                        textAlign: "center",
                                        color:
                                            isMode2 && isActive
                                                ? resolvePrimary(
                                                      categoryDisplaySelectedTextColor
                                                  )
                                                : DEFAULT_DISPLAY_TEXT_COLOR,
                                        textTransform: "uppercase",
                                    }}
                                >
                                    {category.name}
                                </Box>
                            )}
                        </Box>
                    </SwiperSlide>
                );
            })}
        </Swiper>
    );
}
